import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  Ban,
  Bike,
  Clock,
  Eye,
  FileText,
  Pencil,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  SalesDocumentDetail,
  SalesDocumentSummary,
} from '@aeris/contracts'
import {
  formatMoney,
  MONEY_SCALE,
  parseHumanAmount,
  toString,
  ZERO_MONEY,
} from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  ActiveFilters,
  FilterableHeaderCell,
  type ActiveFilterChip,
  RowActions,
  RowOpener,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableActionsCell,
  TableActionsHeaderCell,
  TableRow,
  VisuallyHidden,
} from '@aeris/ui'

import { AuthorisationDialog } from '../components/authorisation-dialog.tsx'
import { AwaitingPickup } from '../components/awaiting-pickup.tsx'
import { CourierDialog } from '../components/courier-dialog.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import {
  PosPaymentDialog,
  type PosTender,
} from '../components/pos-payment-dialog.tsx'
import { ChecksBadges } from '../components/sales-checks-panel.tsx'
import { SalesDocumentDialog } from '../components/sales-document-dialog.tsx'
import { SalesDocumentForm } from '../components/sales-document-form.tsx'
import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import {
  ApiError,
  convertQuote,
  dispatchSalesDocument,
  fetchCredit,
  fetchCurrentCashSession,
  fetchPaymentMethods,
  fetchSalesDocument,
  fetchSalesDocuments,
  fetchSalespeople,
  payAndHoldQuote,
  posCheckout,
  transitionSalesDocument,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { enumValue, rangeLabel } from '../lib/column-filters.ts'
import { customerLookup } from '../lib/customer-lookup.ts'
import { formatDayTime } from '../lib/date-format.ts'
import { readRememberedTerminal } from '../lib/remembered-terminal.ts'
import { SALES_EXPORT } from '../lib/sales-export.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Os orçamentos abertos, do ponto de vista do caixa (F034).
 *
 * O cliente atravessa a loja com um papel na mão e o entrega aqui. Três coisas
 * podem acontecer com aquele papel, e a tela existe para que sejam só essas
 * três:
 *
 *   * **retirar** — ele paga e leva. A cobrança acontece AQUI, com o mesmo
 *     diálogo de recebimento do PDV, e não reabrindo o carrinho: o cliente já
 *     concordou com aquelas linhas e aquele total, e um caixa que redigita a
 *     venda é um caixa que pode digitar diferente. O orçamento é fechado pelo
 *     servidor na mesma transação da venda;
 *   * **pagar e retirar depois** (F218) — ele paga agora e volta para buscar.
 *     O orçamento vira pedido confirmado e pago, a mercadoria fica separada, e
 *     a hora da retirada só é marcada quando ele leva — em "Pagos, esperando
 *     retirada", mais abaixo;
 *   * **entregar** — a mercadoria sai com um motoboy. O dinheiro não sai junto:
 *     nasce um título a receber, e ele é acertado depois, motoboy por motoboy;
 *   * **cancelar** — a mercadoria volta ao estoque, e um supervisor precisa
 *     autorizar (ADR-0026).
 *
 * Foi por não existir o primeiro caminho que a Elite Digital relatou *"fazem
 * orçamento e nunca fazem a venda"*: o documento morria no papel.
 *
 * (Eram três caminhos; o do meio é o quarto, e veio depois.)
 *
 * A busca é pelo NÚMERO, porque é o que está impresso grande no papel e é o
 * que o cliente fala em voz alta. Um caixa com fila não procura por nome.
 */

const PAGE_SIZE = 25

const money = (wire: string) =>
  parseHumanAmount(wire.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY

/** `05/08 14:32` — o dia e a hora, que é o que cabe numa coluna estreita. */
const stamp = (iso: string): string => formatDayTime(iso)

export const OrcamentosPage = (): ReactNode => {
  const { allows, session: authSession } = useAuth()
  const canWrite = allows('sales.write')
  const canTakeMoney = allows('pos.operate') && allows('payment.write')

  const queryClient = useQueryClient()
  const [term, setTerm] = useState('')
  const search = useDebounced(term, 250)
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /*
   * O orçamento aberto para alterar (F147). O vendedor corrige o dele antes de
   * o cliente chegar ao caixa; quem gerencia corrige qualquer um. O servidor
   * diz quem pode, em `editScope`.
   */
  const [editingId, setEditingId] = useState<string | null>(null)
  /** Ver e reimprimir, sem passar pelo formulário de edição (F150). */
  const [viewingId, setViewingId] = useState<string | null>(null)
  /*
   * Ordenação e filtro de coluna (F152).
   *
   * Esta tela não ordenava por NADA — nem por número, nem por valor — e era a
   * única das quatro assim. O servidor sempre aceitou os três; faltava a tela.
   */
  const sort = useTableSort<'issued_at' | 'number' | 'total_amount'>(
    'issued_at',
    'desc',
    () => {
      setPage(1)
    }
  )
  const [salespersonUserId, setSalespersonUserId] = useState('')
  const [totalRange, setTotalRange] = useState<{
    kind: 'range'
    min: string
    max: string
  } | null>(null)
  const editingDocument = useQuery({
    queryKey: ['sales-document', editingId],
    queryFn: ({ signal }) => fetchSalesDocument(editingId ?? '', signal),
    enabled: editingId !== null,
  })
  /** O orçamento cujo cancelamento está esperando a senha de um supervisor. */
  const [cancelling, setCancelling] = useState<SalesDocumentSummary | null>(
    null
  )
  /** O orçamento que está sendo cobrado, já com as linhas. */
  const [paying, setPaying] = useState<SalesDocumentDetail | null>(null)
  /**
   * Se o cliente leva agora ou volta depois (F218). O mesmo diálogo de
   * cobrança, e duas rotas: a venda concluída, ou o pedido pago e guardado.
   */
  const [later, setLater] = useState(false)
  const [delivering, setDelivering] = useState<SalesDocumentSummary | null>(
    null
  )

  const branchId = authSession?.context?.branchId ?? undefined
  const terminalId = readRememberedTerminal()

  /**
   * O caixa aberto deste terminal.
   *
   * O mesmo terminal lembrado do PDV, porque é a mesma máquina no mesmo balcão.
   * Sem caixa aberto não há gaveta para o dinheiro desta retirada, e a tela diz
   * isso antes de deixar alguém começar a cobrar.
   */
  const current = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession({ terminalId, branchId }, signal),
    enabled: terminalId !== '' && canTakeMoney,
  })

  const session = current.data?.session ?? null

  const methods = useQuery({
    queryKey: ['payment-methods', { status: 'active' }],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: canTakeMoney,
  })

  const credit = useQuery({
    queryKey: ['credit', paying?.customerPartyId],
    queryFn: ({ signal }) => fetchCredit(paying?.customerPartyId ?? '', signal),
    enabled: paying !== null,
  })

  /*
   * O saldo de vale de quem retira (F126). Sem ele, escolher "Vale" dizia
   * "Identifique o cliente" num orçamento que JÁ tem cliente.
   */
  const voucher = useQuery({
    queryKey: ['customer-credit', paying?.customerPartyId],
    queryFn: ({ signal }) =>
      fetchCustomerCredit(paying?.customerPartyId ?? '', signal),
    enabled: paying !== null && allows('exchange.read'),
  })

  const salespeople = useQuery({
    queryKey: ['salespeople'],
    queryFn: ({ signal }) => fetchSalespeople(signal),
  })

  const clearFilters = (): void => {
    setTerm('')
    setSalespersonUserId('')
    setTotalRange(null)
    setPage(1)
  }

  const chips: ActiveFilterChip[] = [
    ...(salespersonUserId === ''
      ? []
      : [
          {
            id: 'salesperson',
            label: `Vendedor: ${
              (salespeople.data?.items ?? []).find(
                (person) => person.userId === salespersonUserId
              )?.displayName ?? 'escolhido'
            }`,
            onRemove: () => {
              setSalespersonUserId('')
              setPage(1)
            },
          },
        ]),
    ...(totalRange === null
      ? []
      : [
          {
            id: 'total',
            label: `Valor: ${rangeLabel(totalRange.min, totalRange.max)}`,
            onRemove: () => {
              setTotalRange(null)
              setPage(1)
            },
          },
        ]),
  ]

  /*
   * O filtro, montado uma vez — F158. A lista e a exportação leem daqui, senão
   * o arquivo sai com um filtro diferente do que está na tela.
   */
  const listRequest = {
    kind: 'quote' as const,
    status: 'sent' as const,
    ...(search.trim() === '' ? {} : { search: search.trim() }),
    ...(salespersonUserId === '' ? {} : { salespersonUserId }),
    ...(totalRange?.min === undefined || totalRange.min === ''
      ? {}
      : { totalMin: totalRange.min }),
    ...(totalRange?.max === undefined || totalRange.max === ''
      ? {}
      : { totalMax: totalRange.max }),
    sort: sort.sort,
    direction: sort.direction,
  }

  const quotes = useQuery({
    queryKey: [
      'sales-documents',
      'quotes',
      search,
      salespersonUserId,
      totalRange?.min ?? '',
      totalRange?.max ?? '',
      page,
      ...[sortKey(sort)],
    ],
    queryFn: ({ signal }) =>
      // Paginado (F132): antes a lista parava em 50 sem dizer, e o 51º
      // orçamento do dia simplesmente não existia para o caixa.
      fetchSalesDocuments(
        { ...listRequest, page, pageSize: PAGE_SIZE },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
    void queryClient.invalidateQueries({ queryKey: ['stock'] })
    void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
    void queryClient.invalidateQueries({ queryKey: ['finance'] })
  }

  const fail = (failure: unknown, fallback: string): void => {
    setError(failure instanceof ApiError ? failure.message : fallback)
  }

  /** Abre a cobrança: as linhas do papel, exatamente como estão nele. */
  const open = useMutation({
    mutationFn: async (input: {
      quote: SalesDocumentSummary
      later: boolean
    }) => await fetchSalesDocument(input.quote.id),
    onSuccess: (response, input) => {
      setError(null)
      setLater(input.later)
      setPaying(response.document)
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível abrir o orçamento.')
    },
  })

  const withdraw = useMutation({
    mutationFn: async (input: {
      quote: SalesDocumentDetail
      tenders: readonly PosTender[]
      creditAuthorisationId?: string | null
    }) =>
      await posCheckout({
        cashSessionId: session?.id ?? '',
        customerPartyId: input.quote.customerPartyId,
        /*
         * O vendedor do orçamento segue para a venda (F035).
         *
         * É a razão de o campo ser obrigatório: quem atendeu está no papel que
         * o cliente trouxe, e a venda que nasce dele não pode passar a ser do
         * caixa. Nulo só num orçamento anterior à regra — aí fica quem cobrou,
         * que é o único nome que existe.
         */
        salespersonUserId:
          input.quote.salespersonUserId ?? authSession?.user.id ?? '',
        priceListId: null,
        discountAmount: input.quote.discountAmount,
        notes: null,
        /*
         * O orçamento vai junto, e o servidor o fecha ANTES de a venda tirar
         * do estoque. É ele que está segurando a peça: solta depois, a própria
         * venda seria recusada por falta de estoque que ela mesma reservou.
         */
        quoteId: input.quote.id,
        quoteVersion: input.quote.version,
        // Os preços capturados no papel, enviados como capturados. O servidor
        // não os resolve de novo — ADR-0017.
        lines: input.quote.lines.map((line) => ({
          variantId: line.variantId,
          description: line.description,
          unit: line.unit,
          unitPrice: line.unitPrice,
          priceListId: line.priceListId,
          priceBasis: line.priceBasis,
          quantity: line.quantity,
          discountAmount: line.discountAmount,
        })),
        payments: input.tenders.map((tender) => ({
          paymentMethodId: tender.methodId,
          amount: toString(tender.amount),
        })),
        creditAuthorisationId: input.creditAuthorisationId ?? null,
      }),
    onSuccess: (response) => {
      setError(null)
      setPaying(null)
      setNotice(
        `Venda nº ${formatDocumentNumber(response.document.number, '')} concluída. A mercadoria saiu do estoque.`
      )
      refresh()
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível concluir a venda.')
    },
  })

  /**
   * Pagar agora e retirar depois (F218).
   *
   * Uma chamada só: o servidor converte, confirma e cobra na mesma transação.
   * Nada sai do estoque e nenhuma hora de retirada é gravada — isso é o
   * Retirou, quando o cliente voltar.
   */
  const holdForLater = useMutation({
    mutationFn: async (input: {
      quote: SalesDocumentDetail
      tenders: readonly PosTender[]
      creditAuthorisationId?: string | null
    }) =>
      await payAndHoldQuote(input.quote.id, {
        version: input.quote.version,
        cashSessionId: session?.id ?? null,
        payments: input.tenders.map((tender) => ({
          paymentMethodId: tender.methodId,
          amount: toString(tender.amount),
        })),
        creditAuthorisationId: input.creditAuthorisationId ?? null,
      }),
    onSuccess: (response) => {
      setError(null)
      setPaying(null)
      setNotice(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} pago. A mercadoria fica separada até o cliente buscar — está em "Esperando retirada".`
      )
      refresh()
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível receber o pagamento.')
    },
  })

  /**
   * Entregar: o orçamento vira pedido e o pedido sai com o motoboy.
   *
   * Duas chamadas porque são dois documentos — a conversão cria o pedido e
   * solta a reserva do orçamento; o despacho tira do físico e levanta o
   * título. Cada uma é atômica por si; a segunda falhando deixa um pedido em
   * aberto, que é um estado legítimo e visível em Vendas, e não um pedido
   * entregue que ninguém entregou.
   */
  const deliver = useMutation({
    mutationFn: async (input: {
      quote: SalesDocumentSummary
      courierPartyId: string
    }) => {
      const converted = await convertQuote(input.quote.id, input.quote.version)
      return await dispatchSalesDocument(converted.order.id, {
        version: converted.order.version,
        courierPartyId: input.courierPartyId,
      })
    },
    onSuccess: (response) => {
      setError(null)
      setDelivering(null)
      setNotice(
        `Pedido nº ${formatDocumentNumber(response.document.number, '')} despachado. O título a receber está em Financeiro, e o acerto é feito na tela do motoboy.`
      )
      refresh()
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível despachar a entrega.')
    },
  })

  const cancel = useMutation({
    mutationFn: async (input: {
      quote: SalesDocumentSummary
      authorisationId: string
      reason: string
    }) =>
      await transitionSalesDocument(input.quote.id, {
        version: input.quote.version,
        to: 'cancelled',
        reason: input.reason,
        authorisationId: input.authorisationId,
      }),
    onSuccess: () => {
      setError(null)
      setCancelling(null)
      refresh()
    },
    onError: (failure: unknown) => {
      fail(failure, 'Não foi possível cancelar o orçamento.')
    },
  })

  const items = quotes.data?.items ?? []
  const total = quotes.data?.total ?? 0
  /** Sem caixa aberto não há gaveta: dito antes, e não depois de tentar. */
  const tillClosed = canTakeMoney && session === null

  return (
    <Page>
      <PageHeader
        title="Orçamentos"
        description="O que foi prometido e ainda não foi retirado, e o que já foi pago e o cliente vem buscar. Procure pelo número do papel que o cliente trouxe."
      />

      <ListToolbar>
        <SearchInput
          aria-label="Buscar orçamento pelo número ou pelo cliente"
          placeholder="Número do orçamento, ou nome do cliente"
          value={term}
          autoFocus
          onChange={(event) => {
            setTerm(event.target.value)
            setPage(1)
          }}
        />

        {/* O filtro inteiro, não a página que está na tela (F158). */}
        <ExportMenu
          fileName="orcamentos"
          sheetName="Orçamentos"
          columns={SALES_EXPORT}
          fetchPage={async (exportPage) =>
            await fetchSalesDocuments({
              ...listRequest,
              page: exportPage,
              pageSize: 100,
            })
          }
          pageSize={100}
          total={total}
        />
      </ListToolbar>

      <ActiveFilters chips={chips} onClearAll={clearFilters} />

      {error !== null && <InlineAlert tone="danger">{error}</InlineAlert>}

      {notice !== null && <InlineAlert tone="success">{notice}</InlineAlert>}

      {tillClosed && (
        <InlineAlert tone="warning">
          Nenhum caixa aberto neste terminal. Abra o caixa para receber uma
          retirada — entregar e cancelar continuam disponíveis.
        </InlineAlert>
      )}

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={quotes.isPending}
        none="Nenhum orçamento em aberto"
      />

      <QueryState
        isPending={quotes.isPending}
        isError={quotes.isError}
        error={quotes.error}
        onRetry={() => {
          void quotes.refetch()
        }}
        what="os orçamentos"
        isEmpty={items.length === 0}
        filtered={
          search.trim() !== '' ||
          salespersonUserId !== '' ||
          totalRange !== null
        }
        onClearFilters={clearFilters}
        empty={
          <EmptyState
            icon={<FileText aria-hidden="true" className="h-8 w-8" />}
            title="Nenhum orçamento em aberto"
            description="Orçamentos criados no PDV com F9 aparecem aqui até serem retirados ou cancelados."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <FilterableHeaderCell
                  className="w-20"
                  kind="number"
                  {...sort.column('number')}
                >
                  Nº
                </FilterableHeaderCell>
                <TableHeaderCell>Cliente</TableHeaderCell>
                <FilterableHeaderCell
                  filter={{
                    kind: 'enum',
                    options: (salespeople.data?.items ?? []).map((person) => ({
                      value: person.userId,
                      label: person.displayName,
                    })),
                  }}
                  {...(salespersonUserId === ''
                    ? {}
                    : { value: enumValue(salespersonUserId) })}
                  onFilter={(next) => {
                    setSalespersonUserId(
                      next?.kind === 'enum' ? next.value : ''
                    )
                    setPage(1)
                  }}
                >
                  Vendedor
                </FilterableHeaderCell>
                <FilterableHeaderCell
                  className="w-28"
                  kind="date"
                  {...sort.column('issued_at')}
                >
                  Feito em
                </FilterableHeaderCell>
                <FilterableHeaderCell
                  numeric
                  className="w-28"
                  {...sort.column('total_amount')}
                  filter={{ kind: 'range', unit: 'money' }}
                  {...(totalRange === null ? {} : { value: totalRange })}
                  onFilter={(next) => {
                    setTotalRange(next?.kind === 'range' ? next : null)
                    setPage(1)
                  }}
                >
                  Total
                </FilterableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canWrite ? (
                  <TableActionsHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableActionsHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((quote) => (
                <TableRow
                  key={quote.id}
                  onOpen={() => {
                    setViewingId(quote.id)
                  }}
                >
                  <TableCell className="font-(--font-mono) font-(--weight-semibold) text-(--text-primary)">
                    <RowOpener
                      onOpen={() => {
                        setViewingId(quote.id)
                      }}
                    >
                      {formatDocumentNumber(quote.number)}
                    </RowOpener>
                  </TableCell>
                  <TableCell>{quote.customerName}</TableCell>
                  <TableCell className="text-(--text-secondary)">
                    {quote.salespersonName ?? '—'}
                  </TableCell>
                  <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                    {stamp(quote.issuedAt)}
                  </TableCell>
                  <TableCell numeric>
                    {formatMoney(money(quote.totalAmount))}
                  </TableCell>
                  <TableCell>
                    {/*
                      Dito na lista, e não só no papel: quem está no caixa
                      precisa saber se aquele número está segurando uma peça
                      antes de decidir cancelá-lo.
                    */}
                    <div className="flex flex-col items-start gap-1">
                      <Badge
                        tone={
                          quote.stockState === 'reserved'
                            ? 'warning'
                            : 'neutral'
                        }
                      >
                        {quote.stockState === 'reserved'
                          ? 'separado'
                          : 'só preço'}
                      </Badge>
                      <ChecksBadges document={quote} />
                    </div>
                  </TableCell>
                  {canWrite ? (
                    <TableActionsCell>
                      {/*
                        DUAS ações à vista, e não uma — decisão do dono
                        (16/09): *"entregar com o motoboy deixa ele do lado do
                        retirar"*.

                        A F132 diz um botão principal por linha, e aqui a
                        exceção é do domínio: um orçamento fechado sai da loja
                        por um de DOIS caminhos, e qual deles é o cliente quem
                        diz na hora. Não são "a ação e o resto" — são duas
                        respostas à mesma pergunta, e esconder uma delas no ⋯
                        fazia o caixa abrir um menu para metade dos orçamentos
                        que fecha. Cancelar continua no ⋯: é o que não se deve
                        apertar por engano.
                      */}
                      <RowActions
                        name={`orçamento nº ${formatDocumentNumber(quote.number)}`}
                        primary={
                          canTakeMoney ? (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={open.isPending || session === null}
                                leadingIcon={
                                  <ArrowRight
                                    aria-hidden="true"
                                    className="h-3.5 w-3.5"
                                  />
                                }
                                onClick={() => {
                                  open.mutate({ quote, later: false })
                                }}
                              >
                                Retirar
                              </Button>
                              {/*
                                F218: pagou e volta para buscar. Ao lado do
                                Retirar, porque é a mesma cobrança — só a
                                mercadoria que fica.
                              */}
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={open.isPending || session === null}
                                leadingIcon={
                                  <Clock
                                    aria-hidden="true"
                                    className="h-3.5 w-3.5"
                                  />
                                }
                                onClick={() => {
                                  open.mutate({ quote, later: true })
                                }}
                              >
                                Pagar e retirar depois
                              </Button>
                              {/*
                                Secundário, e não um segundo `primary`: o
                                balcão é o caminho comum, e duas caixas cheias
                                lado a lado não dizem qual é qual.
                              */}
                              <Button
                                variant="secondary"
                                size="sm"
                                leadingIcon={
                                  <Bike
                                    aria-hidden="true"
                                    className="h-3.5 w-3.5"
                                  />
                                }
                                onClick={() => {
                                  setDelivering(quote)
                                }}
                              >
                                Entregar
                              </Button>
                            </>
                          ) : quote.editScope !== 'none' ? (
                            // O vendedor não retira: o que ele faz com o
                            // orçamento é corrigir antes do caixa (F147).
                            <Button
                              variant="secondary"
                              size="sm"
                              leadingIcon={
                                <Pencil
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                              onClick={() => {
                                setEditingId(quote.id)
                              }}
                            >
                              Editar
                            </Button>
                          ) : undefined
                        }
                        items={[
                          {
                            // Ver e reimprimir (F150). Primeiro da lista porque
                            // é a única ação que não muda nada.
                            label: 'Ver detalhes',
                            icon: (
                              <Eye aria-hidden="true" className="h-4 w-4" />
                            ),
                            onSelect: () => {
                              setViewingId(quote.id)
                            },
                          },
                          canTakeMoney &&
                            quote.editScope !== 'none' && {
                              label: 'Editar',
                              icon: (
                                <Pencil
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              ),
                              onSelect: () => {
                                setEditingId(quote.id)
                              },
                            },
                          {
                            label: 'Cancelar',
                            danger: true,
                            icon: (
                              <Ban aria-hidden="true" className="h-4 w-4" />
                            ),
                            onSelect: () => {
                              setCancelling(quote)
                            },
                          },
                        ]}
                      />
                    </TableActionsCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <AwaitingPickup
        cashSessionId={session?.id ?? null}
        onOpen={setViewingId}
        onDone={(message) => {
          setError(null)
          setNotice(message)
        }}
      />

      {paying !== null && (
        <PosPaymentDialog
          total={money(paying.totalAmount)}
          methods={methods.data?.methods ?? []}
          credit={credit.data ?? null}
          voucherBalance={voucher.data?.balance ?? null}
          voucherLookup={customerLookup(voucher, {
            hasCustomer: true,
            allowed: allows('exchange.read'),
          })}
          creditLookup={customerLookup(credit, {
            hasCustomer: true,
            allowed: true,
          })}
          pending={withdraw.isPending || holdForLater.isPending}
          error={error}
          onConfirm={(tenders, creditAuthorisationId) => {
            if (later) {
              holdForLater.mutate({
                quote: paying,
                tenders,
                creditAuthorisationId,
              })
            } else {
              withdraw.mutate({ quote: paying, tenders, creditAuthorisationId })
            }
          }}
          onClose={() => {
            setPaying(null)
          }}
        />
      )}

      {delivering !== null && (
        <CourierDialog
          title={`Entregar o orçamento nº ${formatDocumentNumber(delivering.number, '')}`}
          pending={deliver.isPending}
          error={error}
          onChoose={(courierPartyId) => {
            deliver.mutate({ quote: delivering, courierPartyId })
          }}
          onClose={() => {
            setDelivering(null)
          }}
        />
      )}

      {editingId !== null && editingDocument.data !== undefined ? (
        <SalesDocumentForm
          key={editingId}
          document={editingDocument.data.document}
          onClose={() => {
            setEditingId(null)
          }}
          onSaved={(saved) => {
            // O papel na mão do cliente ficou velho: a tela diz para reimprimir.
            setError(null)
            setNotice(
              `Orçamento nº ${formatDocumentNumber(saved.number, '')} alterado. Reimprima o papel para o cliente levar o valor certo ao caixa.`
            )
            refresh()
          }}
        />
      ) : null}

      {viewingId === null ? null : (
        <SalesDocumentDialog
          documentId={viewingId}
          onClose={() => {
            setViewingId(null)
          }}
          /*
           * Editar aqui exige DUAS coisas: quem pode mexer em orçamento, e um
           * orçamento que ainda aceita mexida. O `editScope` vem do servidor em
           * cada registro — um orçamento já retirado volta `none`, e a
           * permissão sozinha não basta.
           */
          {...(canWrite &&
          items.find((item) => item.id === viewingId)?.editScope !== 'none'
            ? {
                onEdit: () => {
                  setEditingId(viewingId)
                  setViewingId(null)
                },
              }
            : {})}
        />
      )}

      {cancelling !== null && (
        <AuthorisationDialog
          title={`Cancelar o orçamento nº ${formatDocumentNumber(cancelling.number, '')}`}
          description={
            cancelling.stockState === 'reserved'
              ? 'Este orçamento está segurando mercadoria. Ela volta ao estoque, e um supervisor precisa autorizar.'
              : 'Um supervisor precisa autorizar o cancelamento.'
          }
          action="sales.cancel"
          targetType="sales_document"
          targetId={cancelling.id}
          onAuthorised={(authorisationId, reason) => {
            cancel.mutate({ quote: cancelling, authorisationId, reason })
          }}
          onClose={() => {
            setCancelling(null)
          }}
        />
      )}
    </Page>
  )
}
